import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calculator, Minus, Plus, Search, Trash2 } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { baseApi } from '@/Redux/baseApi';
import { invalidateOrder, invalidateOrderLists } from '@/Redux/Slices/operationsApi';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import {
  useListCategoriesQuery,
  useListFullCatalogQuery,
  type CatalogItemRow,
} from '@/Redux/Slices/catalogApi';
import { localizedName } from '@/lib/localized';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useBranches } from '@/Pages/Operations/shared';
import { useOpenVisits, useVisitCheck } from '@/Pages/Spaces/lib/visits';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { StationModeToggle, useStationMode } from '@/components/StationMode';
import CheckDetail from './CheckDetail';
import ItemConfigDialog from './ItemConfigDialog';
import ShiftStrip from './ShiftPanel';
import { apiError, balanceOf, type CashierShift, type Charge, type Check } from './lib';
import {
  addLine,
  cartCount,
  cartTotal,
  changeQuantity,
  lineTotal,
  removeLine,
  type CartLine,
  type LineDraft,
} from './registerCart';
import {
  cartIsHeld,
  holdCart,
  sendCart,
  type CacheEffect,
  type OrderContext,
  type Refusal,
  type RegisterResult,
  type RegisterState,
  type RegisterTarget,
} from './registerFlow';

/**
 * The register — a venue's till, run from the browser.
 *
 * The kitchen board, the waiter station and the kids desk are already station
 * screens here; the cashier was the one post that still needed the POS app
 * installed on a device. This is that post: a menu, a cart, somewhere to send
 * the order, and the bill it opens.
 *
 * ## What it composes rather than rebuilds
 *
 * Almost all of it already existed and none of it is duplicated here:
 *
 * - **`ShiftPanel`** (`ShiftStrip`) — the drawer, its cash movements, its
 *   figures, and count-and-close. It is also the source of truth for whether a
 *   drawer is open at all, which it reports upwards through `onShiftChange`.
 * - **`CheckDetail`** — the whole settle surface, and with it `PaymentDialogs`,
 *   `SplitDialog`, `DiscountDialog` and `PrintableCheck`. Settling here — the
 *   sale just rung up, or the bill the picked visit was already running — is
 *   the same component the till's list and the floor map's Bill pane mount, so
 *   a bill looks the same wherever it is reached from.
 * - **`catalogApi`** — the menu, from disk on a cold start and revalidated
 *   behind it, so a register that lost its uplink mid-shift keeps its grid.
 * - **`StationMode`** — fullscreen, wake lock, and Escape ordered correctly
 *   through the dialog stack.
 *
 * What is genuinely new is the two things the dashboard had nowhere at all:
 * order **entry** (`registerCart` + `ItemConfigDialog`) and the **write
 * sequence** that gets a cart to the kitchen without ever telling the kitchen
 * about lines that are still in this browser (`registerFlow` — read its header
 * before changing anything about how this screen writes).
 *
 * ## The bill and the cart are two lists, never one
 *
 * A visit picked here usually has a bill open already, and a guest walking up
 * to pay for table 5 is the commonest thing that happens at a counter. So the
 * picked target's check is read and drawn *above* the cart — beside it, never
 * merged into it. Merging would be a lie with a cost attached: a cart row
 * carries a minus button, and a cashier must not be offered one on a line the
 * kitchen already has. It would also erase the only distinction this screen
 * exists to keep, which is what has been sent and what is still in this
 * browser. So the bill's list is read-only and carries the balance, the cart's
 * carries the controls and the subtotal, `Take payment` acts on the bill and
 * `Send` on the cart, and neither ever reaches into the other's lines.
 *
 * Until this existed, picking a table with an open check drew an empty cart
 * saying "nothing rung up yet" over a bill owing real money, and greyed out
 * `Take payment` — because settling was wired to the order *this session* had
 * sent. A cashier had no way to take money for a bill this browser had not
 * itself rung up, which is most of the bills a till is handed.
 *
 * ## Permissions
 *
 * Gated on `orders.create`, which is the key that makes this screen useful and
 * the one a CASHIER holds (`roles.ts`). What a cashier does *not* hold shapes
 * the rest of it: no `sessions.create`, so this never opens a table's visit —
 * it orders against, and settles, visits that are already open; no
 * `checks.create`, so it opens no bill of its own — a walk-in bill is still
 * opened from `Billing/Checks`, and a takeaway sale opens its own check
 * server-side with no key needed at all.
 *
 * Reading a bill is `checks.view`, a separate key an account may be short of.
 * Everything above the settle button works without it: the grid rings up, the
 * cart sends, the kitchen gets its ticket. What it cannot do is read a check —
 * so no read is attempted, the bill region says which key is missing instead
 * of drawing an empty shell, `Take payment` stays disabled carrying the same
 * sentence, and `CheckDetail` is never mounted without the key its own loader
 * would spend a 403 on.
 */

/** Where the station-mode flag lives. One per screen, namespaced like the rest. */
const MODE_KEY = 'venue.register.mode';

/**
 * Root font-size in station mode.
 *
 * Smaller than the waiter station's 20px on purpose: the pass is a board read
 * from across a kitchen, and this is a screen somebody is standing at with
 * their hands on it. It is still a step up from the 16px a laptop assumes,
 * because a till is watched over a counter — by the guest as well as the
 * cashier — and every size in the app is rem-based, so one root value reflows
 * the whole scale rather than blurring it the way a transform would.
 */
const STATION_ROOT_FONT = '18px';

type TargetKind = 'session' | 'takeaway' | 'check';

/** Open checks in this branch — the bill a resource session is running, a tab. */
function useOpenChecks(branchId: string | null, enabled: boolean, reloadKey: number) {
  const [checks, setChecks] = useState<Check[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .get<{ data?: Check[] }>('/checks', {
        params: { status: 'OPEN', limit: 100, ...(branchId ? { branchId } : {}) },
      })
      .then(({ data }) => {
        if (!cancelled) setChecks(data?.data ?? []);
      })
      // Without `checks.view` the picker is simply empty, not broken.
      .catch(() => {
        if (!cancelled) setChecks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId, enabled, reloadKey]);

  return checks;
}

/**
 * The bill the picked target is already running, in full.
 *
 * `useOpenChecks` above lists checks and `useVisitCheck` answers which one is a
 * visit's bill, but neither carries charges — and a cashier looking at table 5
 * needs the lines on it, not a number with nothing behind it. So the id comes
 * from those and the check itself is re-read by id.
 *
 * `reloadKey` is the same counter the drawer strip watches. A payment, a
 * refund, or an order of this till's own landing on this bill all move it, and
 * a balance still reading 87.50 after the guest has paid sends them away owing
 * nothing and shows a cashier a debt.
 */
function useOpenBill(checkId: string | null, reloadKey: number) {
  const [bill, setBill] = useState<Check | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!checkId) {
      // Cleared rather than kept: last table's bill left under a freshly picked
      // one is a payment taken against the wrong guest.
      setBill(null);
      setFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    api
      .get<Check>(`/checks/${checkId}`)
      .then(({ data }) => {
        if (!cancelled) setBill(data);
      })
      .catch(() => {
        if (cancelled) return;
        // Said out loud rather than swallowed the way the pickers above swallow
        // theirs. An empty picker is a picker with nothing in it; a silently
        // empty bill region over a table that does have a check is exactly the
        // "nothing rung up yet" lie this region exists to end.
        setBill(null);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [checkId, reloadKey, attempt]);

  return { bill, loading, failed, retry: () => setAttempt((n) => n + 1) };
}

export default function Register() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const dispatch = useAppDispatch();
  const mode = useStationMode(MODE_KEY, { rootFontSize: STATION_ROOT_FONT });

  const branches = useBranches();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);
  const [pickedBranch, setPickedBranch] = useState('');
  /** The screen's own filter wins; the global header is the fallback. */
  const branchId = pickedBranch || activeBranchId || branches[0]?.id || null;
  const { code: currency, format } = useCurrency(branchId);

  const [shift, setShift] = useState<CashierShift | null>(null);
  /** Bumped when money moved, so the drawer strip and the bill list re-read. */
  const [moneyMoved, setMoneyMoved] = useState(0);

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [configuring, setConfiguring] = useState<CatalogItemRow | null>(null);

  const [kind, setKind] = useState<TargetKind>('session');
  const [sessionId, setSessionId] = useState('');
  const [checkId, setCheckId] = useState('');
  const [customerName, setCustomerName] = useState('');

  const [state, setState] = useState<RegisterState>({ order: null, cart: [], held: null });
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  const [settling, setSettling] = useState<string | null>(null);

  const locked = cartIsHeld(state.held);

  const canSend = can('orders.update', 'orders.send');
  const canSettle = can('checks.view');

  // ------------------------------------------------------------ the menu

  const categories = useListCategoriesQuery(
    { limit: 100, isActive: true },
    { refetchOnMountOrArgChange: 60 },
  );
  const catalog = useListFullCatalogQuery({ isActive: true }, { refetchOnMountOrArgChange: 60 });
  /** Hoisted because the query RESULT is a new object every render and its
   *  `refetch` is not — which is what keeps `apply` below a stable callback. */
  const refetchMenu = catalog.refetch;

  /**
   * Category and search applied in memory rather than sent as query params: the
   * whole menu is already in hand (`catalogApi` sweeps every page and writes it
   * to disk), so a chip press and a keystroke are instant instead of being a
   * round trip a cashier watches with a queue behind them. Matching name-or-SKU
   * mirrors `QueryCatalogDto`, so the same term finds the same rows whether the
   * rows came off the wire or off the disk.
   */
  const items = useMemo(() => {
    const rows = catalog.data?.rows ?? [];
    const term = search.trim().toLocaleLowerCase();
    return rows.filter((item) => {
      if (categoryId && item.categoryId !== categoryId) return false;
      if (!term) return true;
      return (
        item.name.toLocaleLowerCase().includes(term) ||
        localizedName(item, i18n.language).toLocaleLowerCase().includes(term) ||
        (item.sku?.toLocaleLowerCase().includes(term) ?? false)
      );
    });
  }, [catalog.data, categoryId, search, i18n.language]);

  // ----------------------------------------------------------- the target

  const { visits } = useOpenVisits(kind === 'session', branchId);
  const openChecks = useOpenChecks(branchId, canSettle, moneyMoved);

  /**
   * Which bill the picked target already has, if any. A visit's is its master
   * check — `Check.isMaster` is per session, so it is read and never guessed —
   * and a bill picked directly is its own answer. Asked only with `checks.view`
   * in hand, so an account without it spends no request on a certain 403.
   */
  const { check: visitBill } = useVisitCheck(
    canSettle && kind === 'session' && sessionId ? sessionId : null,
  );
  const openBillId =
    (kind === 'check' ? checkId : kind === 'session' ? visitBill?.id : null) || null;
  const bill = useOpenBill(canSettle ? openBillId : null, moneyMoved);
  /**
   * Voided charges are left off the strip on purpose: a voided line is money no
   * longer owed, and this list answers what is on the bill. The whole record,
   * void reasons included, is one press away in `CheckDetail`.
   */
  const billLines = (bill.bill?.charges ?? []).filter((charge) => !charge.voidedAt);

  // A branch switch invalidates whatever was picked in the old one.
  const previousBranch = useRef(branchId);
  useEffect(() => {
    if (previousBranch.current === branchId) return;
    previousBranch.current = branchId;
    setSessionId('');
    setCheckId('');
  }, [branchId]);

  const target = useMemo<RegisterTarget | null>(() => {
    if (kind === 'takeaway') return { kind: 'takeaway', customerName };
    if (kind === 'session') return sessionId ? { kind: 'session', sessionId } : null;
    return checkId ? { kind: 'check', checkId } : null;
  }, [kind, sessionId, checkId, customerName]);

  // ------------------------------------------------------------- writing

  /**
   * Apply what the flow decided, in one place.
   *
   * The cache is only ever told about a write the *server* acknowledged: a held
   * entry is on this browser's disk and the server has not seen it, so
   * refetching now would paint rows without it.
   */
  const apply = useCallback(
    (result: RegisterResult) => {
      setState(result.state);
      setNotice(result.notice ? t(result.notice.key, result.notice.values) : null);
      setRefusal(result.refusal);
      if (result.refusal) {
        // The grid is still offering something the kitchen cannot make. Pull a
        // fresh menu behind the screen so it stops.
        void refetchMenu();
      }
      const effects = new Set<CacheEffect>(result.effects);
      if (effects.has('order') && result.state.order) {
        dispatch(invalidateOrder(result.state.order.id));
        dispatch(invalidateOrderLists());
      }
      if (effects.has('sessions')) dispatch(baseApi.util.invalidateTags(['Session']));
      if (effects.has('checks')) setMoneyMoved((n) => n + 1);
    },
    [refetchMenu, dispatch, t],
  );

  const run = useCallback(
    async (action: (context: OrderContext) => Promise<RegisterResult>) => {
      if (!target) return;
      setBusy(true);
      try {
        apply(await action({ branchId, target }));
      } catch (error) {
        toast.error(apiError(error, t('operations.errors.actionFailed')));
      } finally {
        setBusy(false);
      }
    },
    [apply, branchId, t, target],
  );

  const hold = () => void run((context) => holdCart(state, context));
  const send = () => void run((context) => sendCart(state, context));

  const startNew = () => {
    setState({ order: null, cart: [], held: null });
    setNotice(null);
    setRefusal(null);
    setCustomerName('');
    // Whatever is held stays in the queue and still lands on the order it was
    // committed to; this till simply stops waiting on it and rings the next one.
  };

  // -------------------------------------------------------------- the cart

  const add = (draft: LineDraft) => {
    if (locked) {
      setNotice(t('billing.register.cartLocked'));
      return;
    }
    setRefusal(null);
    setState((current) => ({ ...current, cart: addLine(current.cart, draft) }));
  };

  const tap = (item: CatalogItemRow) => {
    if (locked) {
      setNotice(t('billing.register.cartLocked'));
      return;
    }
    // A missing `_count` is read as "ask": one extra dialog costs a press, and a
    // large merged into a small costs the guest the wrong plate and the wrong
    // money. `type` is checked too — a meal has choices `_count` cannot see.
    const counts = item._count;
    const hasChoices =
      !counts || counts.variants > 0 || counts.modifierGroups > 0 || item.type === 'MEAL_COMBO';
    if (hasChoices) {
      setConfiguring(item);
      return;
    }
    add({
      itemId: item.id,
      name: localizedName(item, i18n.language),
      unitPrice: item.basePrice,
    });
  };

  const step = (key: string, delta: number) => {
    if (locked) {
      setNotice(t('billing.register.cartLocked'));
      return;
    }
    setRefusal(null);
    setState((current) => ({ ...current, cart: changeQuantity(current.cart, key, delta) }));
  };

  const drop = (key: string) => {
    if (locked) return;
    setRefusal(null);
    setState((current) => ({ ...current, cart: removeLine(current.cart, key) }));
  };

  const subtotal = cartTotal(state.cart, currency);
  const count = cartCount(state.cart);

  // ------------------------------------------------------------ settling

  /**
   * Which check `Take payment` opens.
   *
   * The bill on screen wins over the order this session sent, and not the other
   * way round: once the target is switched the two are different checks, and
   * settling the one the cashier has stopped looking at takes money against the
   * wrong table. In the ordinary case they agree — an order routed through a
   * visit lands on that visit's master check, which is the bill drawn above.
   * The order stays the answer wherever there is no bill to read: a takeaway,
   * or a visit whose check this very sale is what opens.
   */
  const settleCheckId = bill.bill?.id ?? state.order?.checkId ?? null;

  /**
   * §17 — a payment settles a check. Where the target had no bill of its own,
   * that check only exists once the order has been routed by `send`:
   * `routeCharges` is what fills `Order.checkId`, so a DRAFT genuinely has none
   * and there is nothing to guess at.
   *
   * Usually the row the flow already holds carries it, because both `POST
   * /orders { submit: true }` and `POST /orders/:id/send` answer with the
   * routed order. The one case it does not is a held write a *background* flush
   * delivered — the queue has the server's answer, this screen never saw it —
   * so rather than disable the button on a stale copy, the order is re-read on
   * the press. That is what the POS register does for the same reason.
   */
  const openSettle = async () => {
    if (settleCheckId) {
      setSettling(settleCheckId);
      return;
    }
    if (!state.order) return;
    try {
      const { data } = await api.get<{ checkId?: string | null }>(`/orders/${state.order.id}`);
      if (!data?.checkId) {
        toast.info(t('billing.register.settleNeedsSend'));
        return;
      }
      setState((current) =>
        current.order ? { ...current, order: { ...current.order, checkId: data.checkId } } : current,
      );
      setSettling(data.checkId);
    } catch (error) {
      toast.error(apiError(error, t('billing.register.settleFailed')));
    }
  };

  const afterSettle = useCallback(() => {
    setMoneyMoved((n) => n + 1);
  }, []);

  const branchOptions = branches.length
    ? branches
    : [...new Set(openChecks.map((row) => row.branchId))].map((id) => ({
        id,
        name: id.slice(-6),
      }));

  return (
    <div ref={mode.ref} className={mode.shellClassName}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Calculator className="h-6 w-6 shrink-0" aria-hidden />
            <bdi>{t('billing.register.title')}</bdi>
          </h1>
          <p className="text-sm text-ink-muted">
            <bdi>{t('billing.register.subtitle')}</bdi>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {branchOptions.length > 1 && (
            <Select
              className="w-auto"
              value={pickedBranch}
              onChange={(event) => setPickedBranch(event.target.value)}
              aria-label={t('billing.checks.branch')}
            >
              <option value="">{t('billing.register.branchFromHeader')}</option>
              {branchOptions.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
          <StationModeToggle mode={mode} />
        </div>
      </header>

      {/* The drawer, as one line of status. It is a fact about the branch, not
          about whatever is in the cart, and it is where a shift is opened and
          closed — this screen only reads whether one is open. */}
      <ShiftStrip
        branchId={branchId}
        currency={currency}
        refreshKey={moneyMoved}
        onShiftChange={setShift}
      />

      <div className="grid min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        {/* ------------------------------------------------------ the menu */}
        <Card className="min-w-0">
          <CardContent className="space-y-3 p-4">
            {catalog.data?.fromCache && (
              // The cache is only honest if it says so. These rows may be days
              // old — safe to order from, because no price on this screen is
              // ever sent (§38) — but not safe to pass off as live.
              <p className="text-xs text-ink-muted">
                {t('billing.register.cachedMenu', {
                  when: new Date(catalog.data.fetchedAt).toLocaleString(i18n.language),
                })}
              </p>
            )}

            <div className="relative">
              <Search
                className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-muted"
                aria-hidden
              />
              <Input
                className="ps-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('billing.register.searchItems')}
                aria-label={t('billing.register.searchItems')}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={categoryId === null ? 'default' : 'secondary'}
                onClick={() => setCategoryId(null)}
              >
                {t('billing.register.allCategories')}
              </Button>
              {(categories.data?.rows ?? []).map((category) => (
                <Button
                  key={category.id}
                  size="sm"
                  variant={categoryId === category.id ? 'default' : 'secondary'}
                  onClick={() => setCategoryId(category.id)}
                >
                  {category.name}
                </Button>
              ))}
            </div>

            {catalog.isLoading && !catalog.data ? (
              <LoadingState label={t('common.loading')} />
            ) : catalog.isError && !catalog.data ? (
              // Both the network and the disk came up empty. "No items match"
              // would be a lie — there is a menu, it just is not here.
              <ErrorState
                message={t('billing.register.menuUnavailable')}
                onRetry={() => void catalog.refetch()}
              />
            ) : items.length === 0 ? (
              <EmptyState title={t('billing.register.noItems')} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    // Sold out is refused at the tile as well as at the server:
                    // the cached flag catches most of it here, and the 422 the
                    // cache cannot know about is caught below, in the cart.
                    disabled={item.isSoldOut || locked}
                    onClick={() => tap(item)}
                    className="flex min-h-28 min-w-0 flex-col justify-between gap-2 rounded-xl border-2 border-[var(--border)] p-3 text-start transition hover:border-[var(--primary)] active:scale-[0.98] disabled:opacity-50"
                  >
                    <span className="line-clamp-2 text-base font-medium leading-snug">
                      {localizedName(item, i18n.language)}
                    </span>
                    <span className="shrink-0 text-base font-semibold tabular-nums text-ink-muted">
                      {item.isSoldOut ? (
                        <Badge tone="warning">{t('billing.register.soldOut')}</Badge>
                      ) : (
                        <>
                          {/* A product with sizes is priced "from": the tile
                              shows the base and the size moves it. */}
                          {item._count?.variants
                            ? t('billing.register.priceFrom', { price: format(item.basePrice) })
                            : format(item.basePrice)}
                        </>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ------------------------------------------------------ the cart */}
        <Card className="min-w-0">
          <CardContent className="flex min-w-0 flex-col gap-3 p-4">
            <div className="flex flex-wrap gap-2">
              {(['session', 'takeaway', 'check'] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  className="flex-1"
                  variant={kind === option ? 'default' : 'secondary'}
                  aria-pressed={kind === option}
                  onClick={() => setKind(option)}
                >
                  {t(`billing.register.target.${option}`)}
                </Button>
              ))}
            </div>

            {kind === 'session' && (
              <Field label={t('billing.register.visit')} hint={t('billing.register.visitHint')}>
                <Select value={sessionId} onChange={(event) => setSessionId(event.target.value)}>
                  <option value="">{t('billing.register.pickVisit')}</option>
                  {visits.map((visit) => (
                    <option key={visit.id} value={visit.id}>
                      {/* A visit is named by its table. One with no table has no
                          number of its own — a resource QR opens one — so it is
                          named by the size of the group instead. */}
                      {visit.table?.number
                        ? t('billing.register.tableNumber', { number: visit.table.number })
                        : t('billing.register.unseated', { count: visit.guestCount })}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {kind === 'takeaway' && (
              <Field
                label={t('billing.register.customerName')}
                hint={t('billing.register.customerNameHint')}
              >
                <Input
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  placeholder={t('billing.register.walkIn')}
                />
              </Field>
            )}

            {kind === 'check' && (
              <Field label={t('billing.register.bill')} hint={t('billing.register.billHint')}>
                <Select value={checkId} onChange={(event) => setCheckId(event.target.value)}>
                  <option value="">{t('billing.register.pickCheck')}</option>
                  {openChecks.map((check) => (
                    <option key={check.id} value={check.id}>
                      {t('billing.checks.checkNumber', { number: check.number })}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {/*
              What the picked target already owes, drawn above the cart and
              never folded into it — see the header's note on why these are two
              lists. Without `checks.view` there is nothing to draw and the
              missing key is named, because a cashier who cannot settle needs to
              know that before the guest is standing there.
            */}
            {canSettle ? (
              <OpenBillPanel
                bill={bill.bill}
                lines={billLines}
                loading={bill.loading}
                failed={bill.failed}
                onRetry={bill.retry}
              />
            ) : (
              target &&
              target.kind !== 'takeaway' && (
                <p className="text-xs text-ink-muted">{t('billing.register.billNeedsKeys')}</p>
              )
            )}

            {state.order && (
              <p className="flex flex-wrap items-center gap-2 rounded-lg bg-subtle px-3 py-2 text-sm">
                <span className="font-medium">
                  {t('billing.register.orderNumber', { number: state.order.number ?? '—' })}
                </span>
                {state.order.status && (
                  <Badge tone={toneForStatus(state.order.status)}>
                    {t(`operations.orderStatus.${state.order.status}`)}
                  </Badge>
                )}
              </p>
            )}

            {/* Deliberately not styled as an error. This is what a working cache
                looks like from the inside: the item sold out, or its menu is
                outside its schedule window, and the next move is to swap one
                line — not to worry that the till is broken. */}
            {refusal && (
              <div
                role="status"
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
              >
                <p className="font-medium">{t('billing.register.unavailableTitle')}</p>
                <p className="mt-1">{refusal.message}</p>
                {refusal.lineKey && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    onClick={() => drop(refusal.lineKey as string)}
                  >
                    {t('billing.register.unavailableRemove')}
                  </Button>
                )}
              </div>
            )}

            {notice && (
              <p role="status" className="text-xs text-ink-muted">
                {notice}
              </p>
            )}

            <div className="min-h-0 flex-1">
              {bill.bill && (
                // Two lists need two names, or the second reads as more of the
                // first — which is the merge this screen refuses to make.
                <p className="pb-1 text-xs font-medium text-ink-muted">
                  {t('billing.register.ringingUp')}
                </p>
              )}
              {state.cart.length === 0 ? (
                // "Nothing rung up yet" is true of the cart and false of the
                // table when a bill is open, and it was the whole of what the
                // screen said about a check owing real money.
                <p className="py-6 text-center text-sm text-ink-muted">
                  {bill.bill
                    ? t('billing.register.cartEmptyOnBill')
                    : t('billing.register.cartEmpty')}
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {state.cart.map((line) => (
                    <CartRow
                      key={line.key}
                      line={line}
                      currency={currency}
                      highlighted={refusal?.lineKey === line.key}
                      locked={locked}
                      onStep={step}
                      onDrop={drop}
                    />
                  ))}
                </ul>
              )}
            </div>

            <dl className="space-y-1 border-t pt-3 text-sm">
              <div className="flex items-center justify-between">
                <dt>{t('billing.register.subtotal', { count })}</dt>
                <dd className="text-lg font-semibold tabular-nums">{format(subtotal)}</dd>
              </div>
              {/* The server prices the order (§38) — tax and any service charge
                  are applied there, so this must never claim to be the total. */}
              <p className="text-xs text-ink-muted">{t('billing.register.taxNote')}</p>
            </dl>

            {!target && (
              <p className="text-xs text-[var(--destructive)]">
                {t('billing.register.needsTarget')}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                className="flex-1"
                size="lg"
                disabled={busy || !target || !canSend || (!state.cart.length && !state.held)}
                title={canSend ? undefined : t('billing.register.needsSendKeys')}
                onClick={send}
              >
                {t('billing.register.send')}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                disabled={busy || !target || (!state.cart.length && !state.held)}
                onClick={hold}
              >
                {t('billing.register.hold')}
              </Button>
            </div>

            {/*
              §17 — a CASH payment belongs to the drawer it was taken in.
              `PaymentsService.resolveShiftId` books it to `null` when there is
              no open shift and the payment still succeeds, so the money lands
              nowhere the count can find it. A register refuses that outright;
              the drawer is opened on the strip above.
            */}
            {!shift && (
              <p role="status" className="text-xs text-amber-800 dark:text-amber-300">
                {t('billing.register.noShiftCash')}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="flex-1"
                disabled={busy || !canSettle || (!settleCheckId && !state.order)}
                title={
                  !canSettle
                    ? t('billing.register.billNeedsKeys')
                    : settleCheckId || state.order
                      ? undefined
                      : t('billing.register.settleNeedsSend')
                }
                onClick={() => void openSettle()}
              >
                {t('billing.register.settle')}
              </Button>
              <Button
                variant="ghost"
                disabled={busy || (!state.cart.length && !state.order && !state.held)}
                onClick={startNew}
              >
                {t('billing.register.newSale')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {configuring && (
        <ItemConfigDialog
          item={configuring}
          currency={currency}
          onClose={() => setConfiguring(null)}
          onAdd={add}
        />
      )}

      {settling && (
        <Dialog
          open
          onClose={() => setSettling(null)}
          title={t('billing.register.settleTitle')}
          className="max-w-5xl"
        >
          <CheckDetail
            key={settling}
            checkId={settling}
            checks={openChecks}
            shift={shift}
            cashRequiresShift
            onChanged={afterSettle}
          />
        </Dialog>
      )}
    </div>
  );
}

/**
 * What the picked target already owes, as a read-only strip.
 *
 * Deliberately not a second `CheckDetail`: nothing here pays, voids, splits or
 * discounts, and `Take payment` opens the real surface — so a bill is still
 * acted on in exactly one place and there is no second one to drift from it.
 * All this does is answer the question the screen used to answer wrongly,
 * which is whether this table owes anything at all.
 */
function OpenBillPanel({
  bill,
  lines,
  loading,
  failed,
  onRetry,
}: {
  bill: Check | null;
  lines: Charge[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation();

  if (loading) return <p className="text-xs text-ink-muted">{t('common.loading')}</p>;
  if (failed) {
    return (
      <div className="rounded-lg border border-[var(--border)] p-3 text-xs">
        <p>{t('billing.register.billUnreadable')}</p>
        <Button size="sm" variant="secondary" className="mt-2" onClick={onRetry}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }
  // A visit with no check yet is not a failure and says nothing at all: the
  // sale being rung up now is what opens its bill.
  if (!bill) return null;

  const money = (amount: string) => formatCurrency(amount, bill.currency, i18n.language);

  return (
    <section className="rounded-lg border border-[var(--border)] p-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t('billing.checks.checkNumber', { number: bill.number })}
        </span>
        <Badge tone={toneForStatus(bill.status)}>{t(`billing.status.${bill.status}`)}</Badge>
      </header>
      <p className="mt-0.5 text-xs text-ink-muted">{t('billing.register.onTheBill')}</p>

      {lines.length === 0 ? (
        <p className="mt-2 text-xs text-ink-muted">{t('billing.charges.empty')}</p>
      ) : (
        <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-sm">
          {lines.map((charge) => (
            <li key={charge.id} className="flex items-start justify-between gap-2">
              <span className="min-w-0">
                <bdi>{charge.description}</bdi>
                {Number(charge.quantity) !== 1 && (
                  <span className="text-ink-muted"> ×{charge.quantity}</span>
                )}
              </span>
              <span className="shrink-0 tabular-nums">{money(charge.amount)}</span>
            </li>
          ))}
        </ul>
      )}

      {/* The balance, not the total: what the guest hands over is what is left
          to pay, and a bill part-settled at the table is a routine thing. */}
      <p className="mt-2 flex items-center justify-between gap-2 border-t pt-2 text-sm font-semibold">
        <span>{t('billing.checks.outstanding')}</span>
        <span className="tabular-nums">{money(balanceOf(bill))}</span>
      </p>
    </section>
  );
}

function CartRow({
  line,
  currency,
  highlighted,
  locked,
  onStep,
  onDrop,
}: {
  line: CartLine;
  currency: string;
  highlighted: boolean;
  locked: boolean;
  onStep: (key: string, delta: number) => void;
  onDrop: (key: string) => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <li
      className={
        highlighted
          ? 'rounded-lg bg-amber-50 p-2 text-sm dark:bg-amber-950/50'
          : 'rounded-lg p-2 text-sm'
      }
    >
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{line.name}</span>
          {line.modifiers.length > 0 && (
            <span className="block text-xs text-ink-muted">
              {line.modifiers.map((modifier) => modifier.name).join(' · ')}
            </span>
          )}
          {line.notes && (
            <span className="block text-xs italic text-ink-muted">{line.notes}</span>
          )}
        </span>
        <span className="shrink-0 tabular-nums">
          {formatCurrency(lineTotal(line, currency), currency, i18n.language)}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          disabled={locked}
          aria-label={t('billing.register.decrease')}
          onClick={() => (line.quantity === 1 ? onDrop(line.key) : onStep(line.key, -1))}
        >
          {line.quantity === 1 ? (
            <Trash2 className="h-4 w-4" aria-hidden />
          ) : (
            <Minus className="h-4 w-4" aria-hidden />
          )}
        </Button>
        <span className="w-6 text-center tabular-nums">{line.quantity}</span>
        <Button
          size="sm"
          variant="ghost"
          disabled={locked}
          aria-label={t('billing.register.increase')}
          onClick={() => onStep(line.key, 1)}
        >
          <Plus className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </li>
  );
}
